import React, { useState } from 'react';
import { Home, Menu, Flame } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import StreakCelebrationModal from '../ui/StreakCelebrationModal';
import AdminExamSwitcher from './AdminExamSwitcher';

const getGreeting = (t) => {
  const hour = new Date().getHours();
  if (hour < 12) return t('dashboard.greeting.goodMorning');
  if (hour < 17) return t('dashboard.greeting.goodAfternoon');
  return t('dashboard.greeting.goodEvening');
};

const Header = ({
  user,
  userProgress,
  selectedExamData,
  onToggleSidebar,
  focusMode = false,
  isAdmin = false
}) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const onDashboard = location.pathname.endsWith('/dashboard') || location.pathname.includes('/dashboard');
  const streak = userProgress?.studyStreak ?? userProgress?.stats?.currentStreak ?? 0;
  const examProvider = selectedExamData?.examProvider;
  const examLevel = selectedExamData?.level ?? selectedExamData?.examLevel;
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);

  const handleNavigate = (path) => {
    navigate(path);
  };
    if (focusMode) {
  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        <div className="flex justify-between items-center h-12 relative">
          {/* Center - Practice Mode */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <span className="text-sm font-medium text-gray-400">{t('header.focusMode')}</span>
          </div>

          {/* Right side - Essential controls */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </header>
  );
}
  return (
    <>
    <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40 safe-area-inset-top">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">

          {/* Left side - Mobile sidebar toggle + Greeting on left */}
          <div className="flex items-center flex-shrink-0 gap-3">
            <button
              onClick={onToggleSidebar}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation min-h-[44px] min-w-[44px]"
              aria-label="Open sidebar"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            
            {/* Greeting on left (all screen sizes) */}
            {user && (
              <div className="flex flex-col items-start min-w-0">
                <span className="font-space text-sm sm:text-base font-bold text-black truncate max-w-full">
                  {getGreeting(t)}, {user?.displayName?.split(' ')[0] || t('dashboard.learner')}
                </span>
                {isAdmin ? (
                  <AdminExamSwitcher examProvider={examProvider} examLevel={examLevel} />
                ) : examProvider && examLevel && (
                  <span className="text-xs text-slate-500 font-medium mt-0.5">
                    {String(examProvider).toUpperCase()} {String(examLevel).toUpperCase()}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-2 sm:gap-4">
            {user ? (
              <>
                {/* Language Switcher, hidden on mobile dashboard to save header space */}
                <span className={onDashboard ? 'hidden lg:inline-flex' : undefined}>
                  <LanguageSwitcher />
                </span>

                {/* Streak badge - click to celebrate */}
                <button
                  onClick={() => setShowStreakCelebration(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-full font-bold text-sm shadow-sm cursor-pointer hover:bg-gray-800 active:scale-95 transition-all duration-150"
                  aria-label={`${streak} ${t('streak.celebration.dayStreak')}`}
                >
                  <Flame className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span>{streak} {t('gamification.streakManager.currentStreak.days')}</span>
                </button>

                {/* Dashboard quick nav icon */}
                <Link
                  to="/dashboard"
                  className="flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 text-slate-600 hover:text-black hover:bg-slate-50 transition-all duration-200"
                  aria-label={t('sidebar.dashboard')}
                >
                  <Home className="w-5 h-5" />
                </Link>
              </>
            ) : (
              /* Not logged in - show login button */
              <button
                onClick={() => handleNavigate('/auth')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors touch-manipulation min-h-[44px] text-sm"
              >
                {t('nav.login')}
              </button>
            )}
          </div>
        </div>
      </div>
    </header>

    {showStreakCelebration && (
      <StreakCelebrationModal
        streak={streak}
        onClose={() => setShowStreakCelebration(false)}
      />
    )}
    </>
  );
};

export default Header;